import { ref } from 'vue';
import axios from 'axios';
import { describeUploadFailure } from '@/lib/uploadFailure';

/**
 * Composable for handling document uploads for authenticated teachers
 * Uses direct client-side upload to R2 via presigned URLs
 *
 * @returns {Object} Upload utilities and state
 */
export function useTeacherDocumentUpload() {
    const isUploading = ref(false);
    const uploadProgress = ref(0);
    const uploadError = ref(null);
    const uploadedFile = ref(null);

    /**
     * Get CSRF token from meta tag
     */
    const getCsrfToken = () => {
        return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    };

    /**
     * Upload a document directly to R2 via presigned URL
     *
     * @param {File} file - The file to upload
     * @param {string} type - Document type (dbs, identity, cv, etc.)
     * @param {Object} options - Additional options
     * @returns {Promise<Object>} Upload result with path and URL
     */
    const uploadDocument = async (file, type, options = {}) => {
        isUploading.value = true;
        uploadProgress.value = 0;
        uploadError.value = null;
        uploadedFile.value = null;

        try {
            // Step 1: Get presigned upload URL from backend
            const presignedResponse = await axios.post('/teacher/files/signed-upload-url', {
                filename: file.name,
                type: type,
                content_type: file.type,
            }, {
                headers: {
                    'X-CSRF-TOKEN': getCsrfToken(),
                    'Content-Type': 'application/json',
                },
            });

            if (!presignedResponse.data.success) {
                throw new Error(presignedResponse.data.message || 'Failed to get upload URL');
            }

            const { upload_url, path, filename } = presignedResponse.data.data;

            // Step 2: Upload file directly to R2 using presigned URL
            await axios.put(upload_url, file, {
                headers: {
                    'Content-Type': file.type,
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    }
                },
            });

            const result = {
                path: path,
                filename: filename,
                original_name: file.name,
                size: file.size,
                type: type,
            };

            uploadedFile.value = result;
            uploadProgress.value = 100;

            if (options.onSuccess) {
                options.onSuccess(result);
            }

            return result;

        } catch (error) {
            const errorMessage = describeUploadFailure(error);
            uploadError.value = errorMessage;

            if (options.onError) {
                options.onError(errorMessage);
            }

            throw new Error(errorMessage);

        } finally {
            isUploading.value = false;
        }
    };

    /**
     * Get a temporary download URL for a document
     *
     * @param {string} path - File path in R2
     * @param {string} type - Document type
     * @param {number} expiryMinutes - URL expiry time in minutes (default: 60)
     * @returns {Promise<Object>} Download URL data
     */
    const getDownloadUrl = async (path, type, expiryMinutes = 60) => {
        try {
            const response = await axios.post('/teacher/files/url', {
                path,
                type,
                expiry_minutes: expiryMinutes,
            }, {
                headers: {
                    'X-CSRF-TOKEN': getCsrfToken(),
                }
            });

            if (!response.data.success) {
                throw new Error(response.data.message || 'Failed to generate download URL');
            }

            return response.data.data;

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to get download URL';
            throw new Error(errorMessage);
        }
    };

    /**
     * Delete a document from storage
     *
     * @param {string} path - File path to delete
     * @param {string} type - Document type
     * @returns {Promise<boolean>} Success status
     */
    const deleteDocument = async (path, type) => {
        try {
            const response = await axios.post('/teacher/files/delete', {
                path,
                type,
            }, {
                headers: {
                    'X-CSRF-TOKEN': getCsrfToken(),
                }
            });

            return response.data.success;

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to delete document';
            throw new Error(errorMessage);
        }
    };

    /**
     * Reset upload state
     */
    const resetUploadState = () => {
        isUploading.value = false;
        uploadProgress.value = 0;
        uploadError.value = null;
        uploadedFile.value = null;
    };

    return {
        // State
        isUploading,
        uploadProgress,
        uploadError,
        uploadedFile,

        // Methods
        uploadDocument,
        getDownloadUrl,
        deleteDocument,
        resetUploadState,
    };
}
